import React, { FC, useCallback, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Table } from 'reactstrap';
import { ProductModel } from '../../../../../shared/orders';
import { ActionButtonModal, MaterialIcon, RichMarkdownIt } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { TableKVPair } from '../../../../utils';
import { ProductForm } from '../productForm';
import { PlatformFeeBadge } from '../utils';

interface ProductInfoProps {
  readonly product: ProductModel;
  readonly onUpdate: () => void;
}

const enum ProductInfoMode {
  View,
  Edit,
}

export const ProductInfo: FC<ProductInfoProps> = ({ product, onUpdate }) => {
  const { ts } = useTranslation();
  const [mode, setMode] = useState(ProductInfoMode.View);

  const deleteProduct = useCallback(async () => {
    await api.deleteProductById(product.id);
    onUpdate();
  }, [product.id, onUpdate]);

  if (mode === ProductInfoMode.View) {
    return (
      <Card id="productInfo">
        <CardHeader>
          <div className="card-sub-header">{ts('product_information')}</div>
          <div className="float-end">
            {!product.deletedAt && (
              <>
                <Button
                  className="indent"
                  color="primary"
                  id="edit"
                  onClick={() => {
                    setMode(ProductInfoMode.Edit);
                  }}
                  title={ts('common.edit')}
                >
                  <MaterialIcon name="edit" />
                </Button>
                <ActionButtonModal
                  actionContent="common.delete"
                  buttonContent={<MaterialIcon name="delete" />}
                  className="indent"
                  color="danger"
                  id="delete"
                  onComplete={deleteProduct}
                  title="delete_product"
                >
                  {ts('are_you_sure_you_want_to_delete')} {product.name}?
                </ActionButtonModal>
              </>
            )}
          </div>
        </CardHeader>
        <CardBody>
          <Table>
            <tbody>
              <TableKVPair displayName="name" name="name" value={product.name} />
              <TableKVPair
                displayName="common.display_name"
                name="displayName"
                value={product.displayName}
              />
              <TableKVPair
                displayName="description"
                name="description"
                value={
                  <RichMarkdownIt
                    source={product.description ?? ts('common.no_description_provided')}
                  />
                }
              />
              <TableKVPair
                displayName="minimum_purchase_age"
                name="minAge"
                value={product.minAge ?? ts('none_set')}
              />
              <TableKVPair
                displayName="maximum_purchase_age"
                name="maxAge"
                value={product.maxAge ?? ts('none_set')}
              />
              <TableKVPair displayName="category" name="category" value={product.category.name} />
              <TableKVPair
                displayName="platform_fee"
                name="platformFee"
                value={<PlatformFeeBadge product={product} />}
              />
            </tbody>
          </Table>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="card-sub-header">{ts('product_information')}</div>
        <div className="float-end">
          <Button
            color="danger"
            id="cancel"
            onClick={() => {
              setMode(ProductInfoMode.View);
            }}
            title={ts('common.cancel')}
          >
            <MaterialIcon name="cancel" />
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        <ProductForm
          action={`/api/products/${product.id}`}
          category={product.category}
          method="patch"
          onSuccess={() => {
            onUpdate();
            setMode(ProductInfoMode.View);
          }}
          product={product}
        />
      </CardBody>
    </Card>
  );
};
